import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { validateEmail } from '../../utils/emailValidator';

const PublicTestPage = () => {
  const { publicLink } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGateForm, setShowGateForm] = useState(false);
  const [emailWarning, setEmailWarning] = useState(null); // { suggestion, reason } | null
  const [candidateData, setCandidateData] = useState({
    candidate_name: '',
    candidate_no: '',
    candidate_email: '',
    candidate_whatsapp: ''
  });

  const fetchPublicTest = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/public/test/${publicLink}`);
      
      // Check if test is locked
      if (response.data.is_locked) {
        setTest(response.data);
        setShowGateForm(false);
        // Show locked message and redirect to home after 3 seconds
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
        return;
      }
      
      setTest(response.data);
      setShowGateForm(true);
    } catch (error) {
      console.error('Error fetching public test:', error);
      toast.error('Test not found or no longer available');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [publicLink, navigate]);

  useEffect(() => {
    fetchPublicTest();
  }, [fetchPublicTest]);

  const handleStartTest = async () => {
    // Validation
    if (!candidateData.candidate_name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!candidateData.candidate_email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    // Email typo / format validation. Block submit on suspected typos so the user
    // can either accept the suggested correction or fix manually before proceeding.
    const emailCheck = validateEmail(candidateData.candidate_email);
    if (!emailCheck.ok) {
      setEmailWarning({
        suggestion: emailCheck.suggestion,
        reason: emailCheck.reason,
      });
      toast.error(emailCheck.reason || 'Please enter a valid email address');
      return;
    }
    setEmailWarning(null);
    // WhatsApp validation - must be 11 digits if provided
    if (candidateData.candidate_whatsapp && !/^[0-9]{11}$/.test(candidateData.candidate_whatsapp)) {
      toast.error('WhatsApp number must be exactly 11 digits');
      return;
    }
    // Class Test: Batch No is mandatory
    if (test?.type === 'class_test' && !candidateData.candidate_no.trim()) {
      toast.error('Please enter your Batch No');
      return;
    }

    try {
      const response = await axios.post(`/public/test/${test.id}/start`, candidateData);
      const submissionId = response.data.submission_id;
      
      // Enter fullscreen mode
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(err => {
          console.log('Fullscreen request failed:', err);
        });
      } else if (elem.webkitRequestFullscreen) { /* Safari */
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) { /* IE11 */
        elem.msRequestFullscreen();
      }
      
      // Navigate to exam environment
      navigate(`/exam/writing/public/${submissionId}`);
    } catch (error) {
      // Special handling for duplicate submission (409 Conflict)
      if (error.response?.status === 409) {
        toast.error(error.response.data.detail, { 
          duration: 6000,
          style: {
            background: '#FEF2F2',
            color: '#991B1B',
            border: '1px solid #FCA5A5'
          }
        });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to start test');
      }
    }
  };

  const renderFormattedText = (text) => {
    if (!text) return '';
    
    let formatted = text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/__(.+?)__/g, '<u>$1</u>')
      .replace(/\n/g, '<br/>');
    
    return <div dangerouslySetInnerHTML={{ __html: formatted }} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-main-gradient flex items-center justify-center">
        <div className="text-slate-600">Loading test...</div>
      </div>
    );
  }

  if (!test) {
    return null;
  }

  return (
    <div className="min-h-screen bg-main-gradient">
      {/* Header */}
      <div className="header-gradient">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {/* Radiance Logo - Left */}
            <img 
              src="https://radianceedu.com/wp-content/uploads/2025/11/Logo-Radiance-Education.png" 
              alt="Radiance Education"
              className="h-16 w-auto object-contain"
            />
            
            {/* Title - Center */}
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white mb-2">IELTS Writing Test</h1>
              <p className="text-white opacity-90">Public Test Portal</p>
            </div>
            
            {/* Radiance Logo - Right */}
            <img 
              src="https://radianceedu.com/wp-content/uploads/2025/12/Screenshot-2025-12-04-024009-Photoroom.png" 
              alt="Radiance Education"
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>
      </div>

      {/* Locked Test Message */}
      {test && test.is_locked && (
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="glass-card p-12 text-center">
            <div className="mb-6">
              <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-slate-800 mb-3">The Exam Has Been Closed</h2>
              <p className="text-lg text-slate-600 mb-6">Thank you for your interest.</p>
              <div className="text-sm text-slate-500 bg-slate-50 rounded-lg p-4 inline-block">
                <p className="mb-1">Redirecting to Radiance Education website in <span className="font-bold text-slate-700">3 seconds</span>...</p>
                <p className="text-xs text-slate-400">Please contact the institute for more information.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pre-Exam Gate */}
      {showGateForm && (
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="glass-card p-8">
            <div className="text-center mb-6">
              <FileText className="w-16 h-16 text-purple-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-700 mb-2">{test.title}</h2>
              <p className="text-slate-500">Please fill in your details to begin the test</p>
            </div>

            {/* Candidate Form */}
            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">Full Name*</Label>
                  <Input
                    placeholder="Enter your full name"
                    value={candidateData.candidate_name}
                    onChange={(e) => setCandidateData({ ...candidateData, candidate_name: e.target.value })}
                    className="input-modern"
                  />
                </div>
                {test?.type === 'class_test' && (
                  <div>
                    <Label className="text-slate-700">Batch No*</Label>
                    <Input
                      placeholder="Enter your batch number"
                      value={candidateData.candidate_no}
                      onChange={(e) => setCandidateData({ ...candidateData, candidate_no: e.target.value })}
                      className="input-modern"
                      required
                    />
                  </div>
                )}
                {test?.type !== 'class_test' && (
                  <div>
                    <Label className="text-slate-700">Candidate Number</Label>
                    <Input
                      placeholder="Optional"
                      value={candidateData.candidate_no}
                      onChange={(e) => setCandidateData({ ...candidateData, candidate_no: e.target.value })}
                      className="input-modern"
                    />
                  </div>
                )}
              </div>
              <div>
                <Label className="text-slate-700">Email Address*</Label>
                <Input
                  type="email"
                  placeholder="your.email@example.com"
                  value={candidateData.candidate_email}
                  onChange={(e) => {
                    setCandidateData({ ...candidateData, candidate_email: e.target.value });
                    if (emailWarning) setEmailWarning(null);
                  }}
                  onBlur={() => {
                    if (!candidateData.candidate_email.trim()) return;
                    const check = validateEmail(candidateData.candidate_email);
                    if (!check.ok && check.suggestion) {
                      setEmailWarning({ suggestion: check.suggestion, reason: check.reason });
                    } else {
                      setEmailWarning(null);
                    }
                  }}
                  className={`input-modern ${emailWarning ? 'border-amber-400 focus-visible:ring-amber-300' : ''}`}
                  data-testid="public-test-email-input"
                />
                {emailWarning && emailWarning.suggestion && (
                  <div
                    className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
                    data-testid="email-typo-suggestion"
                  >
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 text-sm text-amber-800">
                      Did you mean{' '}
                      <button
                        type="button"
                        className="font-semibold underline hover:no-underline"
                        onClick={() => {
                          setCandidateData({ ...candidateData, candidate_email: emailWarning.suggestion });
                          setEmailWarning(null);
                        }}
                        data-testid="email-typo-accept-btn"
                      >
                        {emailWarning.suggestion}
                      </button>
                      ? Click to fix.
                    </div>
                  </div>
                )}
                {emailWarning && !emailWarning.suggestion && (
                  <p className="text-xs text-red-600 mt-1" data-testid="email-error-msg">
                    {emailWarning.reason}
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  You will receive your test results at this email
                </p>
              </div>

              {/* WhatsApp Number */}
              <div>
                <Label className="text-slate-700">WhatsApp Number (Optional)</Label>
                <Input
                  type="tel"
                  placeholder="01XXXXXXXXX"
                  maxLength={11}
                  value={candidateData.candidate_whatsapp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                    setCandidateData({ ...candidateData, candidate_whatsapp: value });
                  }}
                  className="input-modern"
                />
                <p className="text-xs text-slate-500 mt-1">
                  11-digit number • Result link will also be sent via WhatsApp
                </p>
              </div>
            </div>

            {/* Test Information */}
            <div className="bg-slate-50 rounded-lg p-6 mb-4">
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-blue-600" />
                Test Information
              </h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-slate-400" />
                  Duration: {test.duration_minutes} minutes
                </li>
                <li>• This test contains 2 tasks</li>
                <li>• Task 1: Write at least 150 words</li>
                <li>• Task 2: Write at least 250 words</li>
                <li>• Timer will start automatically</li>
                <li>• You can submit your answers anytime before the timer expires</li>
              </ul>
            </div>

            {/* Important Rules Warning */}
            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-700 font-bold text-base mb-2">
                    ⚠️ Important Rules - Read Carefully!
                  </p>
                  <ul className="text-red-600 text-sm space-y-1 list-disc pl-4">
                    <li><strong>First tab switch</strong> = Warning</li>
                    <li><strong>Second tab switch</strong> = Your exam will be <strong>auto-submitted</strong></li>
                    <li>Once submitted, you <strong>cannot retake</strong> this exam</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Start Button */}
            <div className="text-center">
              <Button
                onClick={handleStartTest}
                className="px-8 py-3 text-lg font-semibold text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, #E53935 0%, #B71C1C 100%)',
                }}
              >
                Start Test
              </Button>
              <p className="text-xs text-slate-400 mt-3">
                By starting this test, you agree to complete it in one sitting
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicTestPage;
